const REORDER_COOKIE_NAME = 'reorder_items';
const REORDER_COOKIE_DAYS = 30;
const REORDER_DATA_ID = 'ReorderCookieData';
const REORDER_ITEM_SECTION_ID = 'reorder-item-section-rendering';
const REORDER_ITEM_SECTION_CONTAINER_ID =
  'shopify-section-reorder-item-section-rendering';

function normalizeHandles(payload) {
  const handles = Array.isArray(payload?.handles) ? payload.handles : [];

  return Array.from(
    new Set(
      handles
        .map((handle) => (typeof handle === 'string' ? handle.trim() : ''))
        .filter((handle) => handle.length > 0),
    ),
  );
}

function normalizeProperties(rawProperties) {
  if (!rawProperties || typeof rawProperties !== 'object') {
    return {};
  }

  const sanitized = {};

  Object.entries(rawProperties).forEach(([key, value]) => {
    const sanitizedKey = typeof key === 'string' ? key.trim() : '';
    const sanitizedValue = value == null ? '' : String(value).trim();

    if (sanitizedKey && sanitizedValue) {
      sanitized[sanitizedKey] = sanitizedValue;
    }
  });

  return sanitized;
}

function normalizeItems(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];

  return items
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const handle = typeof item.handle === 'string' ? item.handle.trim() : '';
      const variantId = Number(item.variant_id);
      const quantity = Number(item.quantity);

      if (!handle || !Number.isFinite(variantId) || variantId <= 0) {
        return null;
      }

      return {
        handle,
        variant_id: variantId,
        quantity:
          Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1,
        properties: normalizeProperties(item.properties),
      };
    })
    .filter(Boolean);
}

function parsePayloadText(text) {
  try {
    const payload = JSON.parse(text || '{}');
    return {
      handles: normalizeHandles(payload),
      items: normalizeItems(payload),
    };
  } catch {
    return null;
  }
}

function getReorderPayloadFromDom() {
  const payloadElement = document.getElementById(REORDER_DATA_ID);

  if (!payloadElement) {
    return null;
  }

  return parsePayloadText(payloadElement.textContent || '{}');
}

function getReorderPayloadFromCookie() {
  if (!window.wakaCookie) {
    return null;
  }

  const cookieValue = window.wakaCookie.get(REORDER_COOKIE_NAME);

  if (!cookieValue) {
    return null;
  }

  return parsePayloadText(cookieValue);
}

function revealGuestReorderSections() {
  document.querySelectorAll('.reorder-section.hidden').forEach((section) => {
    section.classList.remove('hidden');
  });
}

function setContainersBusy(containers, isBusy) {
  containers.forEach((container) => {
    container.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  });
}

function updateEmptyStatesFromContainers(containers) {
  containers.forEach((container) => {
    const root = container.closest('reorder-products');

    if (!root) {
      return;
    }

    const emptyState = root.querySelector('[data-reorder-empty]');

    if (!emptyState) {
      return;
    }

    const hasItems = container.querySelector('[data-reorder-item]') !== null;
    emptyState.classList.toggle('hidden', hasItems);
  });
}

function syncReorderCookieFromCustomerPayload() {
  if (!window.wakaCookie) {
    return;
  }

  const payload = getReorderPayloadFromDom();

  // Do nothing when payload is not present (guest pages should keep existing cookie).
  if (!payload) {
    return;
  }

  if (payload.handles.length === 0 && payload.items.length === 0) {
    window.wakaCookie.remove(REORDER_COOKIE_NAME);
    return;
  }

  window.wakaCookie.set(
    REORDER_COOKIE_NAME,
    JSON.stringify(payload),
    REORDER_COOKIE_DAYS,
  );
}

function renderProperties(container, properties) {
  if (!container) {
    return;
  }

  const entries = Object.entries(properties || {});

  if (entries.length === 0) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = entries
    .map(
      ([key, value]) =>
        `<div class="body-xs">${escapeHtml(key)}: ${escapeHtml(value)}</div>`,
    )
    .join('');
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function hydrateFetchedItem(itemElement, cookieItem) {
  if (!itemElement) {
    return;
  }

  itemElement.dataset.variantId = String(cookieItem.variant_id);
  itemElement.dataset.quantity = String(cookieItem.quantity);
  itemElement.dataset.properties = JSON.stringify(cookieItem.properties || {});

  const quantityInput = itemElement.querySelector('.input-qty__input');
  if (quantityInput) {
    const resolvedQuantity = String(cookieItem.quantity);
    quantityInput.value = resolvedQuantity;
    quantityInput.setAttribute('value', resolvedQuantity);
  }

  const propertiesContainer = itemElement.querySelector(
    '[data-reorder-properties]',
  );
  renderProperties(propertiesContainer, cookieItem.properties);
}

async function fetchItemMarkup(cookieItem) {
  const requestUrl = new URL(
    `${window.location.origin}/products/${encodeURIComponent(cookieItem.handle)}`,
  );
  requestUrl.searchParams.set('variant', String(cookieItem.variant_id));
  requestUrl.searchParams.set('section_id', REORDER_ITEM_SECTION_ID);

  const response = await fetch(requestUrl.toString(), {
    method: 'GET',
    headers: {
      Accept: 'text/html',
    },
  });

  if (!response.ok) {
    throw new Error(`Reorder item request failed: ${response.status}`);
  }

  const responseText = await response.text();
  const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
  const sectionContent = parsedHtml.getElementById(
    REORDER_ITEM_SECTION_CONTAINER_ID,
  );

  if (!sectionContent) {
    throw new Error('Missing reorder item section content.');
  }

  const article = sectionContent.querySelector('[data-reorder-item]');

  if (!article) {
    throw new Error('Missing reorder item article markup.');
  }

  hydrateFetchedItem(article, cookieItem);
  return article.outerHTML;
}

async function hydrateGuestReorderItemsFromCookie() {
  const reorderItemsContainers = Array.from(
    document.querySelectorAll('[data-reorder-items]'),
  );

  if (reorderItemsContainers.length === 0) {
    return;
  }

  const payloadFromDom = getReorderPayloadFromDom();

  // If payload exists on page, server already rendered customer items.
  if (payloadFromDom) {
    return;
  }

  const payloadFromCookie = getReorderPayloadFromCookie();
  const cookieItems = payloadFromCookie?.items || [];

  if (cookieItems.length === 0) {
    updateEmptyStatesFromContainers(reorderItemsContainers);
    return;
  }

  revealGuestReorderSections();
  setContainersBusy(reorderItemsContainers, true);

  const markups = await Promise.all(
    cookieItems.map(async (item) => {
      try {
        return await fetchItemMarkup(item);
      } catch {
        return '';
      }
    }),
  );

  const mergedMarkup = markups.filter(Boolean).join('');

  if (!mergedMarkup) {
    updateEmptyStatesFromContainers(reorderItemsContainers);
    setContainersBusy(reorderItemsContainers, false);
    return;
  }

  reorderItemsContainers.forEach((container) => {
    const hasServerItems =
      container.querySelector('[data-reorder-item]') !== null;

    if (hasServerItems) {
      return;
    }

    container.innerHTML = mergedMarkup;

    const reorderProductsRoot = container.closest('reorder-products');
    if (reorderProductsRoot) {
      reorderProductsRoot.dispatchEvent(
        new CustomEvent('reorder:items-updated', { bubbles: false }),
      );
    }
  });

  updateEmptyStatesFromContainers(reorderItemsContainers);
  setContainersBusy(reorderItemsContainers, false);
}

async function initReorderCookie() {
  syncReorderCookieFromCustomerPayload();
  await hydrateGuestReorderItemsFromCookie();
}

window.wakaSyncReorderCookie = initReorderCookie;

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      void initReorderCookie();
    },
    { once: true },
  );
} else {
  void initReorderCookie();
}
